import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import { Provider } from 'react-super-responsive-table/dist/utils/tableContext';
import styles from './index.module.scss';

export interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  /**
   * If true, border radius and left/right borders are hidden on mobile resolutions.
   * @default false
   */
  fullWidthOnMobile?: boolean;

  /**
   * Allows you to explicitly specify the table headers that will be displayed in pivoted mode,
   * rather than generating them automatically.
   *
   * Provide the headers as an index-title dictionary.
   */
  headers?: Record<number, string>;

  /**
   * If true, the table is displayed inline in a container and should not have any surrounding
   * padding/borders.
   * @default false
   */
  inline?: boolean;

  /**
   * If true, rows will be highlighted when hovered.
   * @default false
   */
  rowHover?: boolean;

  /**
   * If true, rows will be divided by horizontal lines.
   * @default true
   */
  rowLines?: boolean;
}

// react-super-responsive-table doesn't support forward refs, and we need a ref to the table
// element. We also need dynamic headers. This component has been lifted from the library code so
// we can make our own modifications.

export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (
    { children, className, fullWidthOnMobile, headers, inline, rowHover, rowLines = true, ...rest },
    ref
  ) => {
    const containerClass = classNames(
      styles.table,
      {
        [styles.fullWidthOnMobile]: fullWidthOnMobile,
        [styles.inline]: inline,
        [styles.rowHover]: rowHover,
        [styles.rowLines]: rowLines,
      },
      className,
      'responsiveTable'
    );

    const headersRef = useRef<Record<number, string>>({});

    // react-super-responsive-table (RSRT) works by walking through the table headers on initial
    // mount and writing them to a mutable state object. If we want dynamic headers without forcibly
    // unmounting and remounting the table, we can supply our own headers object that follows the
    // same structure as RSRT. In this case we need to prevent RSRT from mutating our headers
    // object. If we just freeze it we may cause an error to be thrown, so instead we wrap it in a
    // Proxy that transparently turns writes into no-ops.
    useEffect(() => {
      if (!headers) return;

      headersRef.current = new Proxy(headers, {
        set: () => true,
      });
    }, [headers]);

    return (
      <Provider value={headersRef.current}>
        <table {...rest} className={containerClass} ref={ref}>
          {children}
        </table>
      </Provider>
    );
  }
);

Table.displayName = 'Table';
